import { useMemo, useState } from "react";
import { Search, History, Trophy, CornerDownLeft } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAppStore } from "@/store/useAppStore";
import { fuzzyRank } from "@/lib/utils/fuzzy";
import { toast } from "@/components/ui/toast";
import type { ActionType, Combination } from "@/types";
import { cn } from "@/lib/utils/cn";

type Source = "top" | "history";

interface SearchRow {
  combination: Combination;
  source: Source;
  label: string;
}

const SOURCE_META: Record<Source, { icon: React.ComponentType<{ className?: string }>; action: ActionType }> = {
  top: { icon: Trophy, action: "Top10_Click" },
  history: { icon: History, action: "History_Click" },
};

/**
 * Search & Discovery — fuzzy, instant search across tracked combinations with
 * auto-complete and keyboard execution.
 */
export function GlobalSearch({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const combinations = useAppStore((s) => s.combinations);
  const apply = useAppStore((s) => s.applyCombination);
  const [query, setQuery] = useState("");

  const rows = useMemo<SearchRow[]>(
    () =>
      combinations.map((c) => ({
        combination: c,
        source: "top" as const,
        label: c.signature,
      })),
    [combinations],
  );

  const ranked = useMemo(
    () => fuzzyRank(query, rows, (r) => `${r.label} ${r.combination.signature}`).slice(0, 12),
    [query, rows],
  );

  async function execute(row: SearchRow) {
    await apply(row.combination, SOURCE_META[row.source].action);
    toast.success("Applied from search", row.combination.signature);
    onOpenChange(false);
    setQuery("");
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-[20%] max-w-xl translate-y-0 p-0">
        <DialogHeader className="sr-only">
          <DialogTitle>Search combinations</DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <Search className="h-5 w-5 text-muted-foreground/60" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search top, favorites & history…"
            className="h-9 border-0 bg-transparent px-0 text-base shadow-none focus-visible:ring-0"
            onKeyDown={(e) => {
              if (e.key === "Enter" && ranked[0]) execute(ranked[0].item);
            }}
          />
        </div>
        <div className="max-h-80 overflow-y-auto p-2 scrollbar-thin">
          {ranked.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-muted-foreground">No results</p>
          ) : (
            ranked.map(({ item }, i) => {
              const Icon = SOURCE_META[item.source].icon;
              return (
                <button
                  key={`${item.source}-${item.combination.id}`}
                  onClick={() => execute(item)}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition hover:bg-secondary",
                    i === 0 && "bg-secondary/60",
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{item.label}</p>
                    {item.label !== item.combination.signature && (
                      <p className="truncate text-xs text-muted-foreground">{item.combination.signature}</p>
                    )}
                  </div>
                  <Badge variant="secondary" className="capitalize">{item.source}</Badge>
                  {i === 0 && <CornerDownLeft className="h-3.5 w-3.5 text-muted-foreground" />}
                </button>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
